/**
 * Procedural organic geometry helpers: tapered tubes along a spline (bodies, tails, legs,
 * tongues), with optional dorsal/belly vertex-colour gradients. Smooth-shaded, cheap.
 */
import { BufferAttribute, BufferGeometry, CatmullRomCurve3, Color, MeshStandardMaterial, Vector3 } from 'three';

export interface TubeOptions {
  /** Points along the spine (local space). */
  points: Vector3[];
  /** Radius at each point (same length as points) or a function of t (0..1). */
  radius: number[] | ((t: number) => number);
  /** Segments along the length. */
  segments?: number;
  /** Radial segments. */
  radial?: number;
  /** Squash factor: >1 wider than tall (x scale of the cross-section). */
  flatten?: number;
  /** Vertex colours: top colour and belly colour (blended by normal.y). */
  top?: Color;
  belly?: Color;
  /** Close the ends with rounded caps. */
  caps?: boolean;
}

const up = new Vector3(0, 1, 0);
const tmpP = new Vector3();
const tmpT = new Vector3();
const tmpN = new Vector3();
const tmpB = new Vector3();

/** A smooth tapered tube along a Catmull-Rom spline through `points` (typically along Z). */
export function tubeGeometry(o: TubeOptions): BufferGeometry {
  const curve = new CatmullRomCurve3(o.points, false, 'catmullrom', 0.5);
  const segs = o.segments ?? 24;
  const radial = o.radial ?? 12;
  const flat = o.flatten ?? 1;
  const caps = o.caps ?? true;
  const capRings = caps ? 3 : 0;
  const rings = segs + 1 + capRings * 2;
  const radiusAt = (t: number): number => {
    if (typeof o.radius === 'function') return o.radius(t);
    const f = t * (o.radius.length - 1);
    const i = Math.min(o.radius.length - 2, Math.floor(f));
    const k = f - i;
    return o.radius[i] * (1 - k) + o.radius[i + 1] * k;
  };
  const pos: number[] = [];
  const nor: number[] = [];
  const col: number[] = [];
  const idx: number[] = [];
  const hasCol = !!(o.top && o.belly);
  const c = new Color();
  // Build ring descriptors: t along curve, radius multiplier, and axial offset (for caps).
  const ringDesc: Array<{ t: number; rm: number; off: number }> = [];
  for (let i = 0; i < capRings; i++) {
    const a = ((i + 0.5) / (capRings + 0.5)) * Math.PI * 0.5;
    ringDesc.push({ t: 0, rm: Math.sin(a), off: -Math.cos(a) });
  }
  for (let i = 0; i <= segs; i++) ringDesc.push({ t: i / segs, rm: 1, off: 0 });
  for (let i = capRings - 1; i >= 0; i--) {
    const a = ((i + 0.5) / (capRings + 0.5)) * Math.PI * 0.5;
    ringDesc.push({ t: 1, rm: Math.sin(a), off: Math.cos(a) });
  }
  for (let r = 0; r < rings; r++) {
    const d = ringDesc[r];
    curve.getPointAt(d.t, tmpP);
    curve.getTangentAt(d.t, tmpT).normalize();
    tmpN.crossVectors(up, tmpT);
    if (tmpN.lengthSq() < 1e-6) tmpN.set(1, 0, 0);
    tmpN.normalize();
    tmpB.crossVectors(tmpT, tmpN).normalize();
    const rad = radiusAt(d.t);
    const rr = rad * d.rm;
    for (let j = 0; j <= radial; j++) {
      const a = (j / radial) * Math.PI * 2;
      const cx = Math.cos(a) * flat;
      const cy = Math.sin(a);
      const nx = tmpN.x * cx + tmpB.x * cy;
      const ny = tmpN.y * cx + tmpB.y * cy;
      const nz = tmpN.z * cx + tmpB.z * cy;
      pos.push(tmpP.x + nx * rr + tmpT.x * d.off * rad, tmpP.y + ny * rr + tmpT.y * d.off * rad, tmpP.z + nz * rr + tmpT.z * d.off * rad);
      // Normal: blend ring normal with axial for caps.
      let nnx = nx / flat, nny = ny, nnz = nz;
      if (d.off !== 0) {
        nnx = nnx * d.rm + tmpT.x * d.off;
        nny = nny * d.rm + tmpT.y * d.off;
        nnz = nnz * d.rm + tmpT.z * d.off;
      }
      const l = Math.hypot(nnx, nny, nnz) || 1;
      nor.push(nnx / l, nny / l, nnz / l);
      if (hasCol) {
        const k = Math.max(0, Math.min(1, (nny / l) * 0.5 + 0.5));
        c.copy(o.belly!).lerp(o.top!, k * k);
        col.push(c.r, c.g, c.b);
      }
    }
  }
  for (let r = 0; r < rings - 1; r++) {
    for (let j = 0; j < radial; j++) {
      const a = r * (radial + 1) + j;
      const b = a + radial + 1;
      idx.push(a, a + 1, b, b, a + 1, b + 1);
    }
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('normal', new BufferAttribute(new Float32Array(nor), 3));
  if (hasCol) g.setAttribute('color', new BufferAttribute(new Float32Array(col), 3));
  g.setIndex(idx);
  g.computeBoundingSphere();
  return g;
}

/** Straight tube along +Z from 0 to length with a radius profile. */
export function limbGeometry(length: number, radii: number[], radial = 8, segments = 6, top?: Color, belly?: Color): BufferGeometry {
  const pts = [new Vector3(0, 0, 0), new Vector3(0, 0, length * 0.5), new Vector3(0, 0, length)];
  return tubeGeometry({ points: pts, radius: radii, radial, segments, top, belly });
}

/** Add a vertex-colour attribute with a top/belly gradient to an existing geometry (by normal.y). */
export function paintGradient(g: BufferGeometry, top: Color, belly: Color): BufferGeometry {
  const n = g.getAttribute('normal');
  const col = new Float32Array(n.count * 3);
  const c = new Color();
  for (let i = 0; i < n.count; i++) {
    const k = Math.max(0, Math.min(1, n.getY(i) * 0.5 + 0.5));
    c.copy(belly).lerp(top, k * k);
    col[i * 3] = c.r;
    col[i * 3 + 1] = c.g;
    col[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new BufferAttribute(col, 3));
  return g;
}

export interface SkinPattern {
  kind: 'spots' | 'bands' | 'mottle';
  color: string;
  /** Feature frequency (per metre). */
  scale: number;
  /** 0..1 strength. */
  amount?: number;
  /** Only paint the upper half (dorsal). Default true. */
  dorsal?: boolean;
}

/** Darken/tint vertex colours with a procedural skin pattern (spots, cross bands, mottling). */
export function applyPattern(g: BufferGeometry, p: SkinPattern, offset = 0): void {
  const pos = g.getAttribute('position');
  const nor = g.getAttribute('normal');
  const col = g.getAttribute('color');
  if (!col) return;
  const c = new Color();
  const pc = new Color(p.color);
  const f = p.scale;
  const amt = p.amount ?? 0.8;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i) + offset;
    const ny = nor.getY(i);
    const dorsal = p.dorsal === false ? 1 : Math.max(0, Math.min(1, ny * 2 + 0.3));
    let k = 0;
    if (p.kind === 'spots') {
      const v = Math.sin(x * f * 1.7 + 1.3) * Math.sin(y * f * 1.1 + z * f * 0.4) * Math.sin(z * f + x * 0.7);
      k = v > 0.35 ? Math.min(1, (v - 0.35) * 6) : 0;
    } else if (p.kind === 'bands') {
      const v = Math.sin(z * f + Math.sin(x * f * 0.8) * 0.8);
      k = v > 0.4 ? Math.min(1, (v - 0.4) * 5) : 0;
    } else {
      const v = Math.sin(x * f * 2.3 + z * f * 0.7) + Math.sin(z * f * 1.9 - y * f * 1.3) + Math.sin(y * f * 2.9 + x * f);
      k = Math.max(0, Math.min(1, v * 0.5));
    }
    k *= dorsal * amt;
    if (k <= 0) continue;
    c.setRGB(col.getX(i), col.getY(i), col.getZ(i)).lerp(pc, k);
    col.setXYZ(i, c.r, c.g, c.b);
  }
  col.needsUpdate = true;
}

/** Shared (cached) materials for static looks — one material per look, not per creature. */
const matCache = new Map<string, MeshStandardMaterial>();
export function sharedMat(key: string, make: () => MeshStandardMaterial): MeshStandardMaterial {
  let m = matCache.get(key);
  if (!m) {
    matCache.set(key, (m = make()));
    m.userData.evoShared = true; // survives stage teardown (reused next visit)
  }
  return m;
}
